// composables/usePoolaClaims.js
//
// Cross-run data layer for the member's «حجوزاتي» (their Poola claims history).
//
// WHY THIS EXISTS — the bug it fixes:
//   «حجوزاتي» used to be derived client-side in usePoolaOffers as
//   `offersAll.filter(o => o.myClaimedQty > 0)` — a filter over the CURRENT run's
//   cursor-paginated offers. That only ever saw offers already paginated into the
//   current run, so a member's claim on an unloaded page, or in ANY past/ENDED
//   run, silently vanished from «حجوزاتي» even though it renders correctly on the
//   OrderBox page (which reads persisted OrderBoxItem rows). Members reported
//   "my claims don't all show up".
//
//   This composable reads the dedicated cross-run endpoint instead — the single
//   source of truth for a member's claims across EVERY run.
//
// Contract (frozen — verify against, do not guess:
//   ../backend/_bmad-output/sync/briefs/2026-07-11-poola-my-purchases-endpoint-verified.md
//   ../backend/app/JsonApi/V1/PoolaPurchases/PoolaPurchaseResource.php):
//   GET /api/v1/poola-purchases?filter[mine]=1
//   - Sanctum, index-only, member-scoped (the caller's own PURCHASED claims —
//     REFUNDED excluded — across ALL runs), newest-first, cursor-paginated
//     (page[limit] 1–100, default 20; page[after] must be a real purchase id).
//   - filter[mine] is presence-only (send 1); indexQuery is the real gate, so a
//     supplier / non-member gets an empty set, never a 500.
//   - sort / include are NOT supported (400). The card is FLAT (no relationships).
//   - Frozen attribute set: offerId, dealId, displayTitle, photoUrl, supplierName,
//     qty, totalCashCents, totalPoolshareCents, runLabel, runStartAt, runEndAt,
//     createdAt. No status / refund / transaction fields.
//   - poolcreditEarnedCents (int|null) + offerCompleted (bool, default false) are
//     DELIVERED — ../backend/_bmad-output/sync/briefs/
//     2026-07-13-poola-completion-poolcredit-DELIVERED.md. Derived per-claim from
//     committed state: offerCompleted = the claim's offer is SOLD_OUT;
//     poolcreditEarnedCents = this row's returned PoolShare (null for a standalone
//     / all-cash / not-yet-complete claim). Still coerced null-safe below so an
//     older BE (or an in-flight rollout) never renders a raw undefined.
//
// A read-only list: nothing mutates an individual claim, so we hold plain
// deserialized rows in a ref (no pinia-orm model). Cursor accumulation +
// dedupe-by-id mirrors usePoolaOffers; the cursor helper is the shared SSOT.
import { computed, ref } from 'vue';
import { jsonApiClient } from '@/httpConfig/config/axios.js';
import { readPageMeta } from '@/utils/jsonApiCursor.js';

const CLAIMS_ENDPOINT = '/poola-purchases';

// Same default page size as the offers list; the server clamps 1–100.
export const POOLA_CLAIMS_PAGE_LIMIT = 20;

/**
 * usePoolaClaims — the member's own Poola claims across all runs.
 *
 * Lazily loaded (the caller triggers loadInitial the first time «حجوزاتي» is
 * opened) so a member who never opens it pays for no fetch.
 */
export function usePoolaClaims() {
  const claims = ref([]); // accumulated deserialized purchase rows (newest-first)
  const nextCursor = ref(null);
  const hasMore = ref(true);
  const loading = ref(false); // first load / refresh
  const isLoadingMore = ref(false); // pagination
  const error = ref(null);
  const loaded = ref(false); // has an initial fetch resolved at least once
  let abortController = null;

  // Empty ONLY after a load has resolved — before that the caller shows the
  // skeleton, so switching to «حجوزاتي» never flashes the empty state.
  const isEmpty = computed(() => loaded.value && claims.value.length === 0);

  function buildParams(cursor = null) {
    // filter[mine] presence-only (value ignored); NEVER send sort / include (both
    // 400). Keep this object to exactly these keys.
    const params = {
      'filter[mine]': 1,
      'page[limit]': POOLA_CLAIMS_PAGE_LIMIT,
    };
    if (cursor) params['page[after]'] = cursor;
    return params;
  }

  async function fetchPage(reset = false) {
    if (loading.value || (isLoadingMore.value && !reset)) return;

    // Own a per-request controller so a superseded fetch (e.g. a post-claim
    // refresh aborting an in-flight loadMore) can't clobber the live request's
    // flags / cursor in its finally — only the latest request resets.
    const controller = new AbortController();
    if (abortController) abortController.abort();
    abortController = controller;

    try {
      if (reset) {
        loading.value = true;
        error.value = null;
      } else {
        isLoadingMore.value = true;
      }

      const cursor = reset ? null : nextCursor.value;
      const res = await jsonApiClient.get(CLAIMS_ENDPOINT, {
        params: buildParams(cursor),
        signal: controller.signal,
      });

      const doc = res?.data ?? {};
      const rows = doc.data ?? [];
      const { cursor: nc, hasMore: more } = readPageMeta(doc);

      if (reset) claims.value = [];

      if (rows.length) {
        // Reward fields are DELIVERED (2026-07-13-poola-completion-poolcredit-
        // DELIVERED.md) — coerce undefined → safe defaults anyway so a pre-rollout
        // BE (or a cached page) never renders a raw undefined and the reward chip's
        // feature-detect stays reliable.
        rows.forEach((row) => {
          row.poolcreditEarnedCents = row.poolcreditEarnedCents ?? null;
          row.offerCompleted = row.offerCompleted ?? false;
        });
        const seen = new Set(claims.value.map((c) => c.id));
        const fresh = rows.filter((row) => !seen.has(row.id)); // de-dupe across overlapping cursors
        claims.value = [...claims.value, ...fresh];
      }

      nextCursor.value = nc;
      hasMore.value = more;
      loaded.value = true;
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
      if (reset) error.value = err; // a load-more failure leaves the list intact
      // eslint-disable-next-line no-console
      console.error('[usePoolaClaims] fetch error:', err);
    } finally {
      // Only the active (latest) request clears shared state — a superseded
      // request that lost the abort race must not reset the live one's flags.
      if (abortController === controller) {
        loading.value = false;
        isLoadingMore.value = false;
        abortController = null;
      }
    }
  }

  function loadInitial() {
    nextCursor.value = null;
    hasMore.value = true;
    return fetchPage(true);
  }

  function loadMore() {
    if (!hasMore.value || loading.value || isLoadingMore.value) return Promise.resolve();
    return fetchPage(false);
  }

  async function refresh(done) {
    try {
      await loadInitial();
    } finally {
      if (typeof done === 'function') done();
    }
  }

  /**
   * Load once, on the first open of «حجوزاتي». A no-op if a load is already
   * loaded or in flight, so repeated segment switches don't refetch.
   */
  function ensureLoaded() {
    if (loaded.value || loading.value) return Promise.resolve();
    return loadInitial();
  }

  return {
    claims,
    hasMore,
    isLoadingMore,
    loading,
    error,
    loaded,
    isEmpty,
    loadInitial,
    loadMore,
    refresh,
    ensureLoaded,
  };
}
